import React from 'react';
import { LevelInfo } from '../../hooks/useGamification';

interface UserXPBadgeProps {
    totalXp: number;
    level: LevelInfo;
    onClick?: () => void;
}

/**
 * Compact XP badge for header/navbar
 * Shows current level and XP
 */
const UserXPBadge: React.FC<UserXPBadgeProps> = ({ totalXp, level, onClick }) => {
    // Calculate progress to next level
    const currentLevelXP = level.xp_for_current_level;
    const nextLevelXP = level.xp_for_next_level;

    let progressPercent = 100;
    if (nextLevelXP && nextLevelXP > currentLevelXP) {
        progressPercent = ((totalXp - currentLevelXP) / (nextLevelXP - currentLevelXP)) * 100;
    }

    return (
        <button
            onClick={onClick}
            style={{
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                border: '1px solid rgba(102, 126, 234, 0.2)',
                borderRadius: '20px',
                padding: '6px 12px 6px 8px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: onClick ? 'pointer' : 'default',
                transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
                if (onClick) {
                    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)';
                }
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)';
            }}
        >
            {/* Level circle with progress */}
            <div
                style={{
                    position: 'relative',
                    width: '24px',
                    height: '24px',
                }}
            >
                {/* Background circle */}
                <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    style={{ position: 'absolute', top: 0, left: 0 }}
                >
                    <circle
                        cx="12"
                        cy="12"
                        r="10"
                        fill="none"
                        stroke="var(--border-color)"
                        strokeWidth="2"
                    />
                    <circle
                        cx="12"
                        cy="12"
                        r="10"
                        fill="none"
                        stroke="url(#xp-gradient)"
                        strokeWidth="2"
                        strokeDasharray={`${progressPercent * 0.628} 62.8`}
                        strokeLinecap="round"
                        transform="rotate(-90 12 12)"
                    />
                    <defs>
                        <linearGradient id="xp-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#667eea" />
                            <stop offset="100%" stopColor="#764ba2" />
                        </linearGradient>
                    </defs>
                </svg>

                {/* Level number */}
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '10px',
                        fontWeight: 700,
                        color: '#667eea',
                    }}
                >
                    {level.level}
                </div>
            </div>

            {/* XP text */}
            <span
                style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: 'var(--text-main)',
                }}
            >
                {totalXp.toLocaleString()} XP
            </span>
        </button>
    );
};

export default UserXPBadge;
